import type { ReactNode } from 'react';
import { css, cx, mediaQueries } from '@eti/styles';
import { InfoButton } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { ScreenReaderPrice } from '@eti/components';
import type { Ticket } from '../../model';

const ticketPriceWrapperStyles = (isAtBottom: boolean) => css`
  display: flex;
  ${isAtBottom && 'flex-direction: row-reverse;'}
  gap: 8px;
`;

const ticketPriceStyles = (isVersion5WithMultipleTickets: boolean) => css`
  font-size: 1.125rem;
  font-weight: 700;

  @media ${mediaQueries.medium.up} {
    ${isVersion5WithMultipleTickets
      ? 'font-size:1.125rem;line-height: 1.125rem;'
      : 'font-size:1.5rem;line-height: 1.5rem;'};
  }
`;

const priceLabelStyles = (isAtBottom: boolean) => css`
  display: flex;
  ${isAtBottom && 'font-size: 0.875rem; margin-left: auto;'};
  font-weight: 400;

  @media ${mediaQueries.medium.up} {
    font-size: 0.875rem;
    line-height: 1.3125rem;
  }
`;

interface TicketPriceProps {
  children: ReactNode;
  ticketVersion: number;
  isMultipleTickets?: boolean;
}

export const TicketPrice = ({
  children,
  ticketVersion,
  isMultipleTickets = false,
  ...rest
}: TicketPriceProps) => {
  const isVersion5WithMultipleTickets = ticketVersion === 5 && isMultipleTickets;
  return (
    <p className={ticketPriceStyles(isVersion5WithMultipleTickets)} {...rest}>
      {children}
    </p>
  );
};

interface PriceLabelProps {
  children: ReactNode;
  className?: string;
  isAtBottom: boolean;
}

export const PriceLabel = ({ children, className = '', isAtBottom }: PriceLabelProps) => (
  <p className={cx(className, priceLabelStyles(isAtBottom))}>{children}</p>
);

interface PriceInfoLabelProps {
  priceLabelStyle?: string;
  isAtBottom: boolean;
  isMultipleTickets?: boolean;
  ticket: Ticket;
  ticketVersion: number;
}

export const PriceInfoLabel = ({
  priceLabelStyle,
  isAtBottom,
  isMultipleTickets = false,
  ticket,
  ticketVersion,
}: PriceInfoLabelProps) => {
  const { t } = useTranslation();
  const infoButtonLabel = t('ScreenReader.PriceSpecification.InfoButton');

  const { priceDisplayText, pricePerPerson } = ticket;
  const isClickable = ticketVersion === 1 || isAtBottom;

  return (
    <>
      <div className={ticketPriceWrapperStyles(isAtBottom)}>
        <TicketPrice
          aria-hidden
          isMultipleTickets={isMultipleTickets}
          ticketVersion={ticketVersion}
        >
          {pricePerPerson}
        </TicketPrice>
        {isClickable && (
          <InfoButton data-testid="ticket-info-and-price-specification" label={infoButtonLabel} />
        )}
        <ScreenReaderPrice price={pricePerPerson} />
      </div>
      <PriceLabel className={priceLabelStyle} isAtBottom={isAtBottom}>
        {priceDisplayText}
      </PriceLabel>
    </>
  );
};
